import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DxChartModule, DxDateRangeBoxComponent, DxSelectBoxComponent } from 'devextreme-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AggregateType,
    InventoryClient,
    MB51AggregateDto,
    MB51AggregateQuery,
    MB51Reason,
    ThemeService
} from '@services';
import { TimeInterval } from 'devextreme-angular/common/charts';
import {ChartsComponent} from "../../../common/charts.component";

@Component({
    selector: 'app-mb51-charts',
    imports: [
        DxDateRangeBoxComponent,
        DxSelectBoxComponent,
        DxChartModule
    ],
    templateUrl: './mb51-charts.component.html',
    styles: [
        `
            dx-chart {
                height: 350px;
                width: 100%;
            }

            ::ng-deep dx-chart .dxc-markers rect {
                cursor: pointer;
            }
        `
    ]
})
export class MB51ChartsComponent extends ChartsComponent implements OnInit, OnChanges {
    private inventoryClient = inject(InventoryClient);
    private destroyRef = inject(DestroyRef);
    private themeService = inject(ThemeService);
    private router = inject(Router);

    @Input() reason: MB51Reason = MB51Reason.Damage;

    data: MB51AggregateDto[] = [];

    amountColor = '';
    csColor = '';
    palColor = '';
    dspColor = '';
    eaColor = '';
    baselineColor = '';

    get title(): string {
        switch (this.reason) {
            case MB51Reason.Damage: return 'Damage';
            case MB51Reason.Adjustment: return 'Adjustments';
            default: return 'MB51';
        }
    }

    get baselineValue(): number | null {
        if (this.reason === MB51Reason.Damage) {
            switch (this.aggregateValue) {
                case AggregateType.Day: return -1053;
                case AggregateType.Week: return -1053;
                case AggregateType.Month: return -4212;
            }
        } else if (this.reason === MB51Reason.Adjustment) {
            return 8000;
        }
        return null;
    }

    get baselineLabel(): string {
        const v = this.baselineValue;
        return v !== null ? `Baseline ${v.toLocaleString()}` : '';
    }

    get tickInterval(): TimeInterval {
        switch (this.aggregateValue) {
            case AggregateType.Week: return 'week';
            case AggregateType.Month: return 'month';
            default: return 'day';
        }
    }

    ngOnInit(): void {
        this.themeService.isDark$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.initColors());

        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['reason'] && !changes['reason'].firstChange) {
            this.loadData();
        }
    }

    onDateRangeChanged(e: any): void {
        if (e.value && e.value[0]) {
            this.dateRangeFilterValue = e.value;
            this.loadData();
        }
    }

    onAggregateChanged(): void {
        this.loadData();
    }

    formatLabelText = (info: any): string =>
        (info.value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    customizeTooltip = (info: any) => ({
        text: this.formatLabelText(info)
    });

    onPointClick = async (e: any) => {
        const data = e.target?.data;
        if (!data?.minDate || !data?.maxDate) return;

        await this.router.navigate(['/inventory/mb51/all-data'], {
            queryParams: {
                from: data.minDate.toISOString(),
                to: data.maxDate.toISOString()
            }
        });
    };

    private initColors(): void {
        const getColor = (name: string) =>
            getComputedStyle(document.body).getPropertyValue(name).trim();

        this.amountColor = getColor('--bs-info');
        this.csColor = getColor('--bs-green-600');
        this.palColor = getColor('--bs-green-400');
        this.dspColor = getColor('--bs-gray-500');
        this.eaColor = getColor('--bs-gray-600');
        this.baselineColor = getColor('--bs-danger');
    }

    private loadData(): void {
        const query = new MB51AggregateQuery({
            aggregateType: this.aggregateValue,
            reason: this.reason,
            from: this.dateRangeFilterValue[0],
            to: this.dateRangeFilterValue[1],
            timeZoneOffset: (new Date()).getTimezoneOffset()
        });

        this.inventoryClient.getMB51Aggregate(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                this.data = result || [];
                this.updateRanges();
            });
    }
}
